import _ from 'lodash';
import type { Client, ContactAttempt, FhbfMeasure } from '../../../common/types/client';
import type { DecryptedItem } from '../../../common/types/items';
import type { StatisticsFilter } from '../../../common/types/statistics/statistics';
import statistics from '../../../common/helpers/statistics';
import filter from '../../../common/helpers/filter';
import {
  CounselingCenterStatistics,
  CounselingCenterMeasureStatistic,
  CounselingCenterOptionStatistic,
} from '../../../common/types/statistics/fhbfStatistics';
import { Field } from '../../../common/types/form';
import importantDataForm from '../../../clients/detail/fhbfForm/counselingCenter/importantDataForm';
import anamnesisForm from '../../../clients/detail/fhbfForm/counselingCenter/anamnesisForm';
import coreDataForm from '../../../clients/detail/fhbfForm/counselingCenter/coreDataForm';
import onwardPlacementForm from '../../../clients/detail/fhbfForm/counselingCenter/onwardPlacementForm';
import { Documentation } from '../../../common/types/documentation';
import { didMeasureTakePlace, isClientRelevantForStatistic } from '../helpers';

const clientOptionsKeyFieldsMapping: {
  key: keyof CounselingCenterOptionStatistic;
  fields: Field<Client>[];
}[] = [
  {
    key: 'location',
    fields: coreDataForm.fields,
  },
  {
    key: 'targetGroup',
    fields: importantDataForm.fields,
  },
  {
    key: 'age',
    fields: anamnesisForm.fields,
  },
  {
    key: 'origin',
    fields: anamnesisForm.fields,
  },
  {
    key: 'personExercisingViolence',
    fields: importantDataForm.fields,
  },
  {
    key: 'namedViolence',
    fields: importantDataForm.fields,
  },
  {
    key: 'contact',
    fields: importantDataForm.fields,
  },
  {
    key: 'onwardPlacement',
    fields: onwardPlacementForm.fields,
  },
];

const defaultMeasure = {
  durationUnderSixtyMinutes: 0,
  durationOverSixtyMinutes: 0,
  numberOfTypeTelephoneConsultation: 0,
  numberOfTypePersonalConsultation: 0,
  numberOfTypeAccompaniment: 0,
  numberOfTypeOnlineConsultation: 0,
  numberOfTopicDomesticViolence: 0,
  numberOfTopicStalking: 0,
  numberOfTopicBullying: 0,
  numberOfTopicFgm: 0,
  numberOfTopicPsychosocialCounseling: 0,
  numberOfTopicTraumaCounseling: 0,
  numberOfTopicOther: 0,
  numberOfTranslation: 0,
  numberOfRiskQuestionnaire: 0,
};

const getOptionStatistic = (clients: DecryptedItem<Client>[]): CounselingCenterOptionStatistic => {
  return clientOptionsKeyFieldsMapping.reduce(
    (optionStatistic, { key, fields }) => ({
      ...optionStatistic,
      [key]: statistics.getStatisticByOptions(clients, key, fields),
    }),
    {
      location: {},
      targetGroup: {},
      age: {},
      origin: {},
      personExercisingViolence: {},
      namedViolence: {},
      contact: {},
      onwardPlacement: {},
    },
  );
};

const getMeasureStatistics = (
  clients: DecryptedItem<Client>[],
  statisticsFilter: StatisticsFilter,
): CounselingCenterMeasureStatistic => {
  const filteredClients = clients
    .filter((decryptedClient) => {
      const measures = (decryptedClient.payload.measures || []).filter((measure) => didMeasureTakePlace(measure));
      return measures.length > 0;
    })
    .map((decryptedClient) => {
      const measures = (decryptedClient.payload.measures || []).filter((measure: FhbfMeasure) =>
        didMeasureTakePlace(measure),
      );
      return {
        ...decryptedClient,
        payload: {
          ...decryptedClient.payload,
          measures,
        },
      };
    });

  const calculateValue = (hasIncreased: boolean, currentValue: number): number => {
    return hasIncreased ? currentValue + 1 : currentValue;
  };

  const clientMeasureStatistics = filteredClients.reduce(
    (statistics, client) => {
      const numberOfWomenWithOneMeasure = calculateValue(
        (client.payload.measures || []).length === 1,
        statistics.numberOfWomenWithOneMeasure,
      );
      const numberOfWomenWithTwoMeasures = calculateValue(
        (client.payload.measures || []).length === 2,
        statistics.numberOfWomenWithTwoMeasures,
      );
      const numberOfWomenWithThreeMeasures = calculateValue(
        (client.payload.measures || []).length === 3,
        statistics.numberOfWomenWithThreeMeasures,
      );
      const numberOfWomenWithFourMeasures = calculateValue(
        (client.payload.measures || []).length === 4,
        statistics.numberOfWomenWithFourMeasures,
      );
      const numberOfWomenWithFiveAndMoreMeasures = calculateValue(
        (client.payload.measures || []).length >= 5,
        statistics.numberOfWomenWithFiveAndMoreMeasures,
      );
      return {
        numberOfWomenWithOneMeasure,
        numberOfWomenWithTwoMeasures,
        numberOfWomenWithThreeMeasures,
        numberOfWomenWithFourMeasures,
        numberOfWomenWithFiveAndMoreMeasures,
      };
    },
    {
      numberOfWomenWithOneMeasure: 0,
      numberOfWomenWithTwoMeasures: 0,
      numberOfWomenWithThreeMeasures: 0,
      numberOfWomenWithFourMeasures: 0,
      numberOfWomenWithFiveAndMoreMeasures: 0,
    },
  );

  const measuresFilteredByDate: FhbfMeasure[] = _.flatten(
    clients.map((client) => {
      const measures = (client.payload.measures || []).filter((measure) =>
        filter.isInYear(measure.date, statisticsFilter.year),
      );
      return measures;
    }),
  );
  const measuresThatHaveTakenPlace = measuresFilteredByDate.filter(
    (measure) => measure.appointmentDidNotTakePlace === undefined || measure.appointmentDidNotTakePlace === false,
  );

  const measuresThatDidNotTakePlace = measuresFilteredByDate.filter(
    (measure) => measure.appointmentDidNotTakePlace === true,
  );

  const measureStatistics = measuresThatHaveTakenPlace.reduce((statistic, measure) => {
    const durationUnderSixtyMinutes = calculateValue(
      (measure.duration || '') > '0' && (measure.duration || '') < '60',
      statistic.durationUnderSixtyMinutes,
    );
    const durationOverSixtyMinutes = calculateValue(
      (measure.duration || '') > '60',
      statistic.durationOverSixtyMinutes,
    );

    const numberOfTypeTelephoneConsultation = calculateValue(
      measure.type === 'telephoneConsultation',
      statistic.numberOfTypeTelephoneConsultation,
    );
    const numberOfTypePersonalConsultation = calculateValue(
      measure.type === 'personalConsultation',
      statistic.numberOfTypePersonalConsultation,
    );
    const numberOfTypeAccompaniment = calculateValue(
      measure.type === 'accompaniment',
      statistic.numberOfTypeAccompaniment,
    );

    const numberOfTypeOnlineConsultation = calculateValue(
      measure.type === 'onlineConsultation',
      statistic.numberOfTypeOnlineConsultation,
    );

    const numberOfTopicDomesticViolence = calculateValue(
      (measure.topic || []).includes('domesticViolence'),
      statistic.numberOfTopicDomesticViolence,
    );
    const numberOfTopicStalking = calculateValue(
      (measure.topic || []).includes('stalking'),
      statistic.numberOfTopicStalking,
    );
    const numberOfTopicBullying = calculateValue(
      (measure.topic || []).includes('bullying'),
      statistic.numberOfTopicBullying,
    );
    const numberOfTopicFgm = calculateValue((measure.topic || []).includes('fgm'), statistic.numberOfTopicFgm);
    const numberOfTopicPsychosocialCounseling = calculateValue(
      (measure.topic || []).includes('psychosocialCounseling'),
      statistic.numberOfTopicPsychosocialCounseling,
    );
    const numberOfTopicTraumaCounseling = calculateValue(
      (measure.topic || []).includes('traumaCounseling'),
      statistic.numberOfTopicTraumaCounseling,
    );
    const numberOfTopicOther = calculateValue((measure.topic || []).includes('other'), statistic.numberOfTopicOther);

    const numberOfTranslation = calculateValue(measure.translator === true, statistic.numberOfTranslation);
    const numberOfRiskQuestionnaire = calculateValue(
      measure.riskQuestionnaire === true,
      statistic.numberOfRiskQuestionnaire,
    );

    return {
      durationUnderSixtyMinutes,
      durationOverSixtyMinutes,
      numberOfTypeTelephoneConsultation,
      numberOfTypePersonalConsultation,
      numberOfTypeAccompaniment,
      numberOfTypeOnlineConsultation,
      numberOfTopicDomesticViolence,
      numberOfTopicStalking,
      numberOfTopicBullying,
      numberOfTopicFgm,
      numberOfTopicPsychosocialCounseling,
      numberOfTopicTraumaCounseling,
      numberOfTopicOther,
      numberOfTranslation,
      numberOfRiskQuestionnaire,
    };
  }, defaultMeasure);

  return {
    totalNumberOfMeasures: measuresThatHaveTakenPlace.length,
    numberOfAppointmentDidNotTakePlace: measuresThatDidNotTakePlace.length,
    ...clientMeasureStatistics,
    ...measureStatistics,
  };
};

const getNumberOfContactAttempts = (clients: DecryptedItem<Client>[], statisticsFilter: StatisticsFilter): number => {
  const contactAttempts: FhbfMeasure[] = clients.flatMap((client) =>
    (client.payload.contactAttempts || []).filter(
      (contactAttempt: ContactAttempt) => contactAttempt.typeOfContact !== 'noContactPossible',
    ),
  );
  return contactAttempts.length;
};

const getFilteredClients = (
  clients: DecryptedItem<Client>[],
  statisticsFilter: StatisticsFilter,
): DecryptedItem<Client>[] => {
  return clients
    .filter((client) => isClientRelevantForStatistic(client, statisticsFilter.year, statisticsFilter.advisor))
    .map((decryptedClient) => {
      const measures = (decryptedClient.payload.measures || []).filter((measure: FhbfMeasure) =>
        filter.isInYear(measure.date, statisticsFilter.year),
      );
      return {
        ...decryptedClient,
        payload: {
          ...decryptedClient.payload,
          measures,
        },
      };
    });
};

const getShortContacts = (documentation: Documentation[], statisticsFilter: StatisticsFilter): number => {
  const shortContacts = documentation.filter((docu) => {
    const isInDateRange = filter.isInYear(docu.date, statisticsFilter.year);
    const isShortContacts = docu.type === 'shortContact';

    return isInDateRange && isShortContacts;
  });

  return shortContacts.reduce((sum, docu) => sum + (docu.numberOfShortContact || 0), 0);
};

const createCounselingCenterStatistics = (
  clients: DecryptedItem<Client>[],
  documentation: Documentation[],
  statisticsFilter: StatisticsFilter,
): CounselingCenterStatistics => {
  const filteredClients = getFilteredClients(clients, statisticsFilter);
  const numberOfShortContacts = getShortContacts(documentation, statisticsFilter);

  const optionStatistic = getOptionStatistic(filteredClients);
  const measureStatistic = getMeasureStatistics(filteredClients, statisticsFilter);
  const numberOfContactAttempts = getNumberOfContactAttempts(clients, statisticsFilter);

  const numberOfAnonymous = statistics.getNumberOfValues(filteredClients, 'anonymous');
  const numberOfChildren = statistics.getNumberOfValues(filteredClients, 'children');
  const numberOfChildrenDirectViolence = statistics.getSumOfValues(filteredClients, 'directViolence');
  const numberOfChildrenZeroToThreeYears = statistics.getSumOfValues(filteredClients, 'ageOfChildrenZeroToThreeYears');
  const numberOfChildrenFourToSixYears = statistics.getSumOfValues(filteredClients, 'ageOfChildrenFourToSixYears');
  const numberOfChildrenSevenToThirteenYears = statistics.getSumOfValues(
    filteredClients,
    'ageOfChildrenSevenToThirteenYears',
  );
  const numberOfChildrenFourteenToSeventeenYears = statistics.getSumOfValues(
    filteredClients,
    'ageOfChildrenFourteenToSeventeenYears',
  );
  const numberOfStabilizationGroup = statistics.getNumberOfValues(filteredClients, 'stabilizationGroup');
  const numberOfTraumaCounseling = statistics.getNumberOfValues(filteredClients, 'traumaCounseling');

  const clientsWithOnwardPlacementDirectContact = filteredClients.filter(
    ({ payload }) =>
      payload.onwardPlacementDirectContact === true &&
      (payload.onwardPlacementContactDetails === undefined || payload.onwardPlacementContactDetails === false) &&
      (payload.onwardPlacement === undefined || payload.onwardPlacement.length === 0),
  );
  const clientsWithOnwardPlacementContactDetails = filteredClients.filter(
    ({ payload }) =>
      (payload.onwardPlacementDirectContact === undefined || payload.onwardPlacementDirectContact === false) &&
      (payload.onwardPlacementContactDetails === true ||
        (payload.onwardPlacement !== undefined && payload.onwardPlacement.length > 0)),
  );
  const clientsWithBothOnwardPlacement = filteredClients.filter(
    ({ payload }) =>
      payload.onwardPlacementDirectContact === true &&
      (payload.onwardPlacementContactDetails === true ||
        (payload.onwardPlacement !== undefined && payload.onwardPlacement.length > 0)),
  );

  return {
    type: 'counselingCenter',
    totalNumber: filteredClients.length,
    numberOfAnonymous,
    numberOfChildren,
    numberOfChildrenDirectViolence,
    numberOfChildrenZeroToThreeYears,
    numberOfChildrenFourToSixYears,
    numberOfChildrenSevenToThirteenYears,
    numberOfChildrenFourteenToSeventeenYears,
    numberOfStabilizationGroup,
    numberOfTraumaCounseling,
    numberOfContactAttempts: numberOfContactAttempts + numberOfShortContacts,
    numberOfOnwardPlacementDirectContact: clientsWithOnwardPlacementDirectContact.length,
    numberOfOnwardPlacementContactDetails: clientsWithOnwardPlacementContactDetails.length,
    numberOfBothOnwardPlacement: clientsWithBothOnwardPlacement.length,
    ...optionStatistic,
    ...measureStatistic,
  };
};

export { clientOptionsKeyFieldsMapping };

export default createCounselingCenterStatistics;
